import { mainAxios } from '@organization/api/axios/mainAxios'
import { useQueryParams } from '@shared/hooks'
import { camelToSnake } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { App, Button, ButtonProps } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { AxiosRequestConfig } from 'axios'
import { DocumentDownload } from 'iconsax-react'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'

type Props = {
  url: string
  params?: AnyObject
  buttonName?: React.ReactNode
  useQueryParams?: boolean
  exportType?: 'excel' | 'csv'
  notifyEmail?: string
  validateFn?: () => Promise<void>
  onError?: (error: unknown) => void
  onDownload?: () => AxiosRequestConfig | undefined
} & ButtonProps

export const ExportFile = ({
  url,
  params: initParams,
  buttonName,
  useQueryParams: initUseQueryParams = true,
  exportType,
  notifyEmail,
  validateFn,
  onError,
  onDownload,
  ...props
}: Props) => {
  const { t } = useTranslation()
  const params = useQueryParams()
  const { message } = App.useApp()

  const { mutate, isPending } = useMutation({
    mutationFn: async (config?: AxiosRequestConfig) => {
      const query = initUseQueryParams
        ? Object.fromEntries(
            Object.entries(params).map(([key, value]) => [
              camelToSnake(key),
              value
            ])
          )
        : {}

      await validateFn?.()

      const { data: configData, ...restConfig } = config || {}

      const bodyData: AnyObject = {}
      if (exportType) {
        bodyData.export_type = exportType
      }
      if (notifyEmail) {
        bodyData.notify_email = notifyEmail
      }
      if (configData) {
        Object.assign(bodyData, configData)
      }

      return await mainAxios({
        url: queryString.stringifyUrl({
          url,
          query: { ...query, ...initParams }
        }),
        method: 'POST',
        data: bodyData,
        responseType: 'blob',
        ...restConfig
      })
    },

    onSuccess: () => message.success(t('download_started')),
    onError
  })

  return (
    <Button
      type="primary"
      icon={<DocumentDownload size="16" />}
      onClick={() => {
        const config = onDownload?.()
        mutate(config)
      }}
      loading={isPending}
      {...props}
    >
      {buttonName ?? t('download')}
    </Button>
  )
}
